import React from 'react';
import { observer } from 'mobx-react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TafButton, TafHeader, TafMenu, TafMenuItem } from '@taf-ui/react';

import { KeycloakInstanceWrapper } from 'Keycloak/Keycloak';
import AppStorage from '../Storage/Storage';
import Language from '../enums/Language';
import Labels from '../enums/Labels';
import Brand from '../enums/Brand';
import LanguageOptions from '../enums/LanguageOptions';
import {
  changeHTMLDocumentLanguage,
  changePageTitle,
  isBrand,
  shiftNLToTheTopOfNationalitiesList,
  sortNationalities,
  translate,
} from '../utils/utils';
import Requests from '../Requests/Requests';
import NationalityService from '../Requests/NationalityService';
import LanguageSelector from '../LanguageSelector/LanguageSelector';
import wefoxLogo from '../images/wefox.png';
import DHMRLogo from '../images/dhmrLogo.png';
import { ProductSpecificData } from '../interfaces/ProductConfig';
import TAFLogo from '../images/logo-taf.png';

import './header.css';

export interface Props {
  isLangSelectorShown: boolean;
  redirectHome: () => void;
}

const LogoutButton = ({
  backgroundMode = 'dark',
  label,
}: {
  backgroundMode: 'dark' | 'light';
  label: string;
}): React.ReactElement => (
  <>
    {!!KeycloakInstanceWrapper.getInstance()?.authenticated && (
      <div
        id="logoutBtn"
        className={`${backgroundMode === 'light' ? 'light-bg' : ''} button-log-out`}
        onClick={() => {
          /* eslint-disable-next-line @typescript-eslint/no-floating-promises */
          KeycloakInstanceWrapper.getInstance().logout();
        }}
      >
        <FontAwesomeIcon icon="sign-out-alt" />
        {label}
      </div>
    )}
  </>
);

class Header extends React.PureComponent<Props> {
  private readonly storage: AppStorage;

  private readonly nationalityService: NationalityService;

  private readonly isProd: boolean;

  // eslint-disable-next-line @typescript-eslint/ban-types
  constructor(props: Props) {
    super(props);
    this.storage = AppStorage.getInstance();
    this.isProd = process.env.NODE_ENV === 'production';
    this.nationalityService = new NationalityService();

    window.addEventListener('tafRoute', () => {
      this.props.redirectHome();
    });
  }

  componentWillUnmount() {
    window.removeEventListener('tafRoute', () => null);
  }

  changeLangHandler = (event: LanguageOptions): void => {
    const lang: Language = event.value;
    this.saveLocale(lang);
    changeHTMLDocumentLanguage(lang);
    changePageTitle(lang);
  };

  saveLocale = (lang: Language): void => {
    void Requests.saveLocale(lang).then(() => {
      this.storage.setLocale(lang);
      this.getNationalities();
      this.getBrandSpecificInformation();
    });
  };

  getBrandSpecificInformation = (): void => {
    Requests.getProductConfig()
      .then((response) => {
        const {
          progressBarLabels,
          instructions,
          documentsInstructions,
          ...rest
        } = response.data.result;
        this.storage.setProductSpecificProgressBarLabels(progressBarLabels);
        this.storage.setProductSpecificInstructionsPage(instructions);
        this.storage.setProductSpecificDocumentsInstructions(
          documentsInstructions,
        );
        this.storage.setProductSpecificInfo(rest as ProductSpecificData);
      })
      .catch((err) => {
        console.error(err);
      });
  };

  getNationalities = (): void => {
    void this.nationalityService
      .fetchNationalities(this.storage.getLocale)
      .then((nationalities) => {
        const sortedNationalities = sortNationalities(
          nationalities,
          'displayName',
        );
        shiftNLToTheTopOfNationalitiesList(sortedNationalities);

        this.storage.setNationalities(sortedNationalities);
      });
  };

  deleteDraft = (): void => {
    void Requests.deleteHealthDeclarationDraft().then((response) => {
      console.log(response); // eslint-disable-line no-console
    });
  };

  render(): React.ReactElement {
    const personalData = this.storage.getPersonalDataFormState;
    const productSpecificData = this.storage.getProductSpecificInfo;

    const greeting =
      personalData.gender === 'MALE'
        ? translate(Labels.MR, this.storage.getLocale)
        : translate(Labels.MS, this.storage.getLocale);

    const { isLangSelectorShown } = this.props;

    const isWefox = isBrand(Brand.wefox);
    const isDHMR = isBrand(Brand.DHMR);
    const salutation = isWefox
      ? personalData.firstName
      : `${greeting} ${personalData.surname || ''}`;
    const isTAF = !isWefox && !isDHMR;

    return (
      <TafHeader>
        {isWefox ? (
          <div className="logo-container" slot="logo-container">
            <img src={wefoxLogo} alt="wefox" />
          </div>
        ) : null}
        {isDHMR ? (
          <div className="logo-container" slot="logo-container">
            <img src={DHMRLogo} alt="DHMR" />
          </div>
        ) : null}
        {isTAF ? (
          <div className="logo-container" slot="logo-container">
            <img src={TAFLogo} alt="TAF" />
          </div>
        ) : null}

        <TafMenu slot="menu" noHoverState={true} noBorders={true}>
          <TafMenuItem>
            <div className="header-centered-block user-info">
              <div>
                <div className="user">
                  <FontAwesomeIcon icon="user" />
                </div>
                {salutation}
              </div>
            </div>
          </TafMenuItem>
          <TafMenuItem>
            <div
              className={cx('language', {
                'language-hidden': !isLangSelectorShown,
              })}
            >
              <LanguageSelector
                value={this.storage.getLocale}
                changeLangHandler={this.changeLangHandler}
              />
            </div>
          </TafMenuItem>
          <TafMenuItem>
            <div className="header-centered-block contacts">
              <FontAwesomeIcon icon="phone-alt" />
              <a href={`tel:${productSpecificData.telephoneNumberHref}`}>
                {productSpecificData.telephoneNumber}
              </a>
            </div>
          </TafMenuItem>
          <TafMenuItem>
            <div className="header-centered-block log-out">
              <LogoutButton
                backgroundMode="dark"
                label={translate(Labels.LOG_OUT, this.storage.getLocale)}
              />
            </div>
          </TafMenuItem>
          {!this.isProd && (
            <div>
              <TafButton
                id="deleteDraft"
                onClick={this.deleteDraft}
                type="button"
              >
                delete draft
              </TafButton>
            </div>
          )}
        </TafMenu>
        <div className="toggle-menu-wrapper" slot="toggle-menu">
          <TafMenu className="toggle-menu" noBorders={true} noHoverState={true}>
            <TafMenuItem>
              <div className="small-screen-header-item user-info">
                <FontAwesomeIcon icon="user" />
                {salutation}
              </div>
            </TafMenuItem>
            {isLangSelectorShown && (
              <TafMenuItem>
                <div
                  className={cx('language small-screen-header-item', {
                    'language-hidden': !isLangSelectorShown,
                  })}
                >
                  <LanguageSelector
                    value={this.storage.getLocale}
                    changeLangHandler={this.changeLangHandler}
                  />
                </div>
              </TafMenuItem>
            )}
            <TafMenuItem>
              <div className="small-screen-header-item contacts">
                <a href={`tel:${productSpecificData.telephoneNumberHref}`}>
                  <FontAwesomeIcon icon="phone-alt" />
                  {productSpecificData.telephoneNumber}
                </a>
              </div>
            </TafMenuItem>
            <TafMenuItem>
              <div className="small-screen-header-item log-out">
                <LogoutButton
                  backgroundMode="light"
                  label={translate(Labels.LOG_OUT, this.storage.getLocale)}
                />
              </div>
            </TafMenuItem>
          </TafMenu>
        </div>
      </TafHeader>
    );
  }
}

export default observer(Header);
